import {
  AdditiveBlending,
  Color,
  InstancedBufferAttribute,
  InstancedMesh,
  NormalBlending,
  PlaneGeometry,
  ShaderMaterial,
  type Texture,
} from 'three'

/**
 * A pool of camera-facing particle quads with **per-instance alpha**.
 *
 * 🔴 This exists because the obvious thing does not work. `InstancedMesh` gives
 * every instance its own colour but a single shared material, so there is no
 * per-instance opacity — and the first pass at drawing these systems folded the
 * fade into `instanceColor` instead, multiplying the tint by the alpha.
 *
 * That is wrong in a way that is easy to talk yourself into and obvious on
 * screen. Against `NormalBlending` a cloud sprite at alpha 0.2 is not a faint
 * cloud, it is a **dark grey** one: the texture still covers the pixel at full
 * opacity, and all the multiply did was darken its colour towards black. The
 * road trail came out as a line of dirt specks trailing each bus. Additive
 * systems happen to survive the trick — for them `colour * alpha` really is
 * dimmer — which is exactly why it looked plausible in the one case that was
 * checked first.
 *
 * So alpha travels as its own instanced attribute and is applied to the
 * fragment's alpha, where it belongs. The shader is deliberately the smallest
 * thing that does that: sample the map, multiply rgb by the per-instance tint,
 * multiply a by the per-instance alpha.
 *
 * `_ADD` materials in the export are Unity's additive particle variant, which
 * ignores destination alpha; `AdditiveBlending` here matches, and the alpha
 * attribute still scales how much light each sprite adds.
 */
const WHOLE_MAP: readonly [number, number, number, number] = [1, 1, 0, 0]

export class ParticlePool {
  readonly mesh: InstancedMesh
  private readonly alphas: InstancedBufferAttribute
  /**
   * Per instance: `[scaleX, scaleY, offsetX, offsetY]` into the flipbook sheet.
   *
   * 🔴 `cloud_2x2_hardshadow` is a sheet of **four** clouds and `UVModule` picks
   * one per particle. Drawing the whole map instead puts all four on every quad,
   * which on screen reads as clusters of grey specks trailing each bus rather
   * than as smoke — and is easy to misdiagnose as a broken texture or a broken
   * alpha, both of which were suspected first. See `ParticleSpec.sheet`.
   *
   * ⚠️ Keep prose backticks **out** of the GLSL below: the shaders are template
   * literals, and a backtick in a comment ends the string.
   */
  private readonly frames: InstancedBufferAttribute
  private readonly geometry: PlaneGeometry
  private readonly material: ShaderMaterial
  private cursor = 0

  constructor(texture: Texture, additive: boolean, capacity: number, name: string) {
    this.geometry = new PlaneGeometry(1, 1)
    this.alphas = new InstancedBufferAttribute(new Float32Array(capacity), 1)
    // `setUsage` is left at the default: the whole buffer is rewritten every
    // frame anyway, so a hint either way changes nothing measurable.
    this.geometry.setAttribute('aAlpha', this.alphas)
    // Defaults to the whole map, which is what a 1x1 sheet wants.
    const frames = new Float32Array(capacity * 4)
    for (let i = 0; i < capacity; i++) {
      frames[i * 4] = 1
      frames[i * 4 + 1] = 1
    }
    this.frames = new InstancedBufferAttribute(frames, 4)
    this.geometry.setAttribute('aFrame', this.frames)

    this.material = new ShaderMaterial({
      uniforms: { map: { value: texture } },
      transparent: true,
      depthWrite: false,
      blending: additive ? AdditiveBlending : NormalBlending,
      vertexShader: `
        attribute float aAlpha;
        attribute vec4 aFrame;
        varying vec2 vUv;
        varying float vAlpha;
        varying vec3 vTint;
        void main() {
          // The flipbook cell for this instance. See ParticleSpec.sheet.
          vUv = uv * aFrame.xy + aFrame.zw;
          vAlpha = aAlpha;
          #ifdef USE_INSTANCING_COLOR
            vTint = instanceColor;
          #else
            vTint = vec3(1.0);
          #endif
          gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D map;
        varying vec2 vUv;
        varying float vAlpha;
        varying vec3 vTint;
        void main() {
          vec4 texel = texture2D(map, vUv);
          gl_FragColor = vec4(texel.rgb * vTint, texel.a * vAlpha);
          if (gl_FragColor.a <= 0.001) discard;
        }
      `,
    })

    this.mesh = new InstancedMesh(this.geometry, this.material, capacity)
    this.mesh.name = name
    this.mesh.count = 0
    // Particles live in world space and are placed by matrix, so the pool's own
    // bounding box is meaningless — leaving culling on hides whole systems the
    // moment the pool's origin leaves the frustum.
    this.mesh.frustumCulled = false
    // Force the instanced colour attribute into existence so `USE_INSTANCING_COLOR`
    // is defined; without a first write three.js never allocates it and the tint
    // silently falls back to white.
    this.mesh.setColorAt(0, new Color(0xffffff))
  }

  /** Start a frame. */
  begin(): void {
    this.cursor = 0
  }

  /** Place one particle. Returns false once the pool is full. */
  push(
    matrix: Parameters<InstancedMesh['setMatrixAt']>[1],
    tint: Color,
    alpha: number,
    frame: readonly [number, number, number, number] = WHOLE_MAP,
  ): boolean {
    if (this.cursor >= this.mesh.instanceMatrix.count) return false
    const i = this.cursor++
    this.mesh.setMatrixAt(i, matrix)
    this.mesh.setColorAt(i, tint)
    this.alphas.setX(i, alpha)
    this.frames.setXYZW(i, frame[0], frame[1], frame[2], frame[3])
    return true
  }

  /** Finish a frame and upload. */
  end(): void {
    this.mesh.count = this.cursor
    this.mesh.instanceMatrix.needsUpdate = true
    this.alphas.needsUpdate = true
    this.frames.needsUpdate = true
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true
  }

  dispose(): void {
    this.geometry.dispose()
    this.material.dispose()
    this.mesh.removeFromParent()
  }
}
